import { useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { titles } from '@/mocks/titles';
import { searchTitles } from '@/utils/titles';
import Breadcrumb from '@/components/base/Breadcrumb';
import TitleRow from '@/components/feature/TitleRow';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get('q') || '';
  const [input, setInput] = useState(keyword);

  const results = useMemo(() => searchTitles(titles, keyword), [keyword]);

  usePageMeta({
    title: keyword ? `搜索「${keyword}」 - 秋霞电影网` : '搜索影视 - 秋霞电影网',
    description: keyword
      ? `秋霞电影网关于「${keyword}」的搜索结果，共 ${results.length} 条相关影视内容。`
      : '在秋霞电影网搜索你感兴趣的电影与电视剧。',
    keywords: keyword ? `${keyword},影视搜索,秋霞电影网` : '影视搜索,秋霞电影网',
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = input.trim();
    if (!trimmed) {
      setSearchParams({});
      return;
    }
    setSearchParams({ q: trimmed });
  };

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '搜索结果' }]} />

      <div className="mt-4 mb-6">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">搜索结果</h1>
        <p className="mt-2 text-sm md:text-base text-foreground-600">
          {keyword ? (
            <>
              关键词「<span className="text-accent-700 font-semibold">{keyword}</span>」共找到{' '}
              <span className="text-primary-700 font-semibold">{results.length}</span> 条结果
            </>
          ) : (
            '输入影视名称关键词开始搜索'
          )}
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="rounded-lg bg-background-100 border border-background-200 p-3 md:p-4 mb-6 flex flex-col sm:flex-row gap-3"
      >
        <div className="flex-1 flex items-center bg-background-50 border border-background-200 rounded-md pl-3 pr-2 h-11 focus-within:border-primary-400">
          <i className="ri-search-line text-foreground-500"></i>
          <input
            type="text"
            name="q"
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder="搜索电影或电视剧名称，如「长夜追光」"
            className="flex-1 bg-transparent px-2 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none"
          />
        </div>
        <button
          type="submit"
          className="h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          搜索
        </button>
      </form>

      {keyword && results.length > 0 && (
        <div className="flex flex-col gap-3 md:gap-4">
          {results.map((item) => (
            <TitleRow key={item.id} item={item} />
          ))}
        </div>
      )}

      {keyword && results.length === 0 && (
        <div className="py-16 flex flex-col items-center text-center">
          <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 text-3xl mb-4">
            <i className="ri-search-eye-line"></i>
          </span>
          <p className="text-foreground-700 mb-1">未找到相关影视，换个关键词试试吧</p>
          <p className="text-sm text-foreground-500 mb-6">可以试试「悬疑」「科幻」等类型词，或直接浏览分类页</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link
              to="/category/movie"
              className="h-10 px-5 flex items-center rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              浏览电影分类
            </Link>
            <Link
              to="/category/tv"
              className="h-10 px-5 flex items-center rounded-md border border-background-300 text-foreground-700 text-sm font-medium hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              浏览电视剧分类
            </Link>
          </div>
        </div>
      )}

      {!keyword && (
        <div className="py-14 flex flex-col items-center text-center">
          <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 text-3xl mb-4">
            <i className="ri-search-line"></i>
          </span>
          <p className="text-foreground-700">还没有输入搜索关键词</p>
        </div>
      )}
    </div>
  );
}